import { Grid } from "@mui/material";
import { styled } from "@mui/material/styles";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import { tableCellClasses } from "@mui/material/TableCell";
import {
  Divider,
  Stack,
  Tooltip,
  Typography,
  Button,
  TextField,
  Autocomplete,
  ChevronLeftIcon,
  ExpandMoreIcon,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from "../../mui";
import React, { Fragment, useEffect, useState, useRef } from "react";
import EditIcon from "@mui/icons-material/Edit";
import TextareaAutosize from "@mui/base/TextareaAutosize";
import ShipmentInquiriesService from "../../services/ShipmentInquiriesService";
import { snackbarActions } from "../../Store/snackbar";
import { useDispatch } from "react-redux";
import { CSVLink } from "react-csv";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.selectedNavItemHover.bgColor,
    color: theme.palette.selectedNavItem.color,
    "& .MuiTableCell-root": {
      border: "1px solid rgba(224, 224, 224, 1)",
    },
  },
}));

const CustomAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  "&.MuiAccordionSummary-root.Mui-expanded": {
    height: "30px",
    minHeight: "30px",
    backgroundColor: "#f1f1f1",
  },
}));

const historyTableColumns = ["Edit Date", "Status Change", "User", "Comment"];

const InquiryDetails = ({
  value,
  setValue,
  formData,
  setFormData,
  getTableData,
}) => {
  const [editMode, setEditMode] = useState(false);
  const [comment, setComment] = useState("");
  const [updateStatus, setUpdateStatus] = useState("");
  const [cohortInvCode, setCohortInvCode] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [trackingNumber, setTrackingNumber] = useState("");
  const [historyTableData, setHistoryTableData] = useState([]);
  const [rowsPerPageLarge, setRowsPerPageLarge] = useState(6);
  const [pageLarge, setPageLarge] = useState(0);
  const dispatch = useDispatch();
  const [csvData, setCsvData] = useState([]);
  const csvLinkRef = useRef(null);
  const rejectStatus = "biorep_rejected";

  const handleChangeRowsPerPageLarge = (event) => {
    setRowsPerPageLarge(+event.target.value);
    setPageLarge(0);
  };

  const handleChangePageLarge = (event, newPage) => {
    setPageLarge(newPage);
  };

  const handleTrackingNumberChange = (tnvalue) => {
    setTrackingNumber(tnvalue);
  };

  const handleCommentChange = (event) => {
    setComment(event.target.value);
  };

  const handleStatusChange = (value) => {
    setUpdateStatus(value);
  };

  const handleBack = () => {
    setValue("one");
    setUpdateStatus("");
    setComment("");
    setTrackingNumber("");
    setFieldErrors({});
    setEditMode(false);
  };

  const commonDispatch = (type, message) => {
    dispatch(
      snackbarActions.showNotification({
        snackbarOpen: true,
        snackbarType: type,
        snackbarMessage: message,
      })
    );
  };

  const handleSubmit = () => {
    if (!isFormValid()) {
      commonDispatch("error", "Please fill all required fields");
      return;
    } else {
      if (formData.status === "shipped") {
        formData.tracking_number = trackingNumber;
      }
      const updatedFormData = {
        ...formData,
        status: updateStatus,
        comment: comment,
        cohorts: getSelectedInventoryCodes(cohortInvCode, formData.cohorts),
      };
      ShipmentInquiriesService.updateOrder(updatedFormData)
        .then((resp) => {
          setUpdateStatus(""); // Reset updateStatus
          setComment(""); // Reset comment
          setTrackingNumber(""); // Reset comment
          commonDispatch("success", resp.data);
          setValue("one");
          getTableData(); // Call the new prop to update table data
        })
        .catch((err) => {
          commonDispatch("error", err.message);
        });
    }
  };

  const isFormValid = () => {
    const errors = {};
    let isValid = true;
    // Check main form fields
    Object.keys(formData).forEach((key) => {
      if (
        key !== "created_at" &&
        key !== "modified_at" &&
        key !== "cohorts" &&
        key !== "address_line_two" &&
        key !== "last_modified_status"
      ) {
        if (formData[key] === "" || formData[key] === null) {
          errors[key] = true;
          isValid = false;
        }
      }
    });

    // Check status and comment
    if (updateStatus === "") {
      errors.updateStatus = true;
      isValid = false;
    }

    if (comment === "" && updateStatus === rejectStatus) {
      errors.comment = true;
      isValid = false;
    }

    // Check cohorts validation
    formData.cohorts.forEach((cohort, cohortIndex) => {
      const bioSampleType = cohort.bio_sample_type;

      Object.keys(cohort).forEach((key) => {
        // Skip these fields from validation
        if (key !== "id" && key !== "inquiry_id" && key !== "cohort_id") {
          if (key === "inventory_code") {
            // Check if inventory_code array is empty or contains empty values
            if (
              !Array.isArray(cohort[key]) ||
              cohort[key].length === 0 ||
              cohort[key].some((code) => code === "")
            ) {
              errors[`cohort_${cohortIndex}_${key}`] = true;
              isValid = false;
            }
          } else {
            // Check if the field is required based on bio_sample_type
            if (isCohortFieldRequired(key, bioSampleType)) {
              if (cohort[key] === "" || cohort[key] === undefined) {
                errors[`cohort_${cohortIndex}_${key}`] = true;
                isValid = false;
              }
            }
          }
        }
      });
    });

    setFieldErrors(errors);
    return isValid;
  };

  const handleSendToBAH = () => {
    const payload = {
      inquiry_id: formData.inquiry_id,
      status: formData.status,
      comment: comment,
      cohorts: formData.cohorts.map((cohort) => ({
        cohortId: cohort.cohort_id,
        inventory_code: cohort.inventory_code,
      })),
    };
    ShipmentInquiriesService.updateStatusToBAH(payload)
      .then((resp) => {
        setValue("one");
        getTableData(); // Call the new prop to update table data
        commonDispatch("success", resp.data);
      })
      .catch((err) => {
        commonDispatch("error", err.message);
      });
  };

  const handleInventoryCodeChange = (
    e,
    cohortIndex,
    invCodeIndex,
    isChecked
  ) => {
    const newValue = e.target.value;
    const newObj = {};
    newObj[newValue] = isChecked;
    setFormData((prevFormData) => {
      const newFormData = { ...prevFormData };
      newFormData.cohorts[cohortIndex].inventory_code[invCodeIndex] = newObj;
      return newFormData;
    });
    getSelectedInventoryCodes(cohortInvCode, formData.cohorts);
  };

  const handleSelectInventoryCode = (cohortId, invCode) => {
    const invCodeKey = Object.keys(invCode)[0];

    setFormData((prevFormData) => {
      const newFormData = { ...prevFormData };
      const cohortIndex = newFormData.cohorts.findIndex(
        (c) => c.cohort_id === cohortId
      );

      if (cohortIndex !== -1) {
        const invCodeIndex = newFormData.cohorts[
          cohortIndex
        ].inventory_code.findIndex(
          (code) => Object.keys(code)[0] === invCodeKey
        );

        if (invCodeIndex !== -1) {
          // Toggle the boolean value
          newFormData.cohorts[cohortIndex].inventory_code[invCodeIndex] = {
            [invCodeKey]: !invCode[invCodeKey],
          };
        }
      }

      return newFormData;
    });
  };

  const handleSelectAllCohortsInventoryCodes = (e, cohortId) => {
    const isChecked = e.target.checked;

    setFormData((prevFormData) => {
      const newFormData = { ...prevFormData };
      const cohortIndex = newFormData.cohorts.findIndex(
        (c) => c.cohort_id === cohortId
      );

      if (cohortIndex !== -1) {
        // Update all inventory codes in the cohort
        newFormData.cohorts[cohortIndex].inventory_code = newFormData.cohorts[
          cohortIndex
        ].inventory_code.map((invCode) => {
          const invCodeKey = Object.keys(invCode)[0];
          return { [invCodeKey]: isChecked };
        });
      }

      return newFormData;
    });
  };

  const handleSelectEveryInventoryCodes = (e) => {
    const isChecked = e.target.checked;

    setFormData((prevFormData) => {
      const newFormData = { ...prevFormData };

      // Update all cohorts' inventory codes
      newFormData.cohorts = newFormData.cohorts.map((cohort) => ({
        ...cohort,
        inventory_code: cohort.inventory_code.map((invCode) => {
          const invCodeKey = Object.keys(invCode)[0];
          return { [invCodeKey]: isChecked };
        }),
      }));

      return newFormData;
    });
  };

  const getSelectedInventoryCodes = (selectedInvcodeObj, cohortsArr) => {
    const updatedCohorts = JSON.parse(JSON.stringify(cohortsArr));
    updatedCohorts.forEach((cohort) => {
      Object.entries(selectedInvcodeObj).forEach(([key, value]) => {
        if (key === cohort.cohort_id) {
          cohort.inventory_code = value;
        }
      });
      // const selectedCodes = selectedInvcodeObj[cohort.cohort_id];
      // cohort.inventory_code = selectedCodes ? [...selectedCodes] : [];
    });
    return updatedCohorts;
  };

  const isCohortFieldRequired = (cohortElement, bioSampleType) => {
    const dnaRequiredFields = [
      "aliquot_concentration",
      "dna_total_yield_perunit",
      "dna_total_yield_perunit_uom",
    ];

    const nonDnaRequiredFields = [
      "number_of_units",
      "volume_per_unit",
      "volume_per_unit_uom",
    ];

    if (bioSampleType === "DNA") {
      return dnaRequiredFields.includes(cohortElement);
    } else {
      return nonDnaRequiredFields.includes(cohortElement);
    }
  };

  const handleFormFieldsChange = (e, fieldType, cohort, i) => {
    if (fieldType === "cohorts") {
      const updatedCohorts = [...formData.cohorts];
      updatedCohorts[i] = { ...cohort, [e.target.name]: e.target.value };
      setFormData({
        ...formData,
        cohorts: updatedCohorts,
      });
    } else {
      setFormData({
        ...formData,
        [e.target.name]: e.target.value,
      });
    }
  };

  const splitByCapital = (label) => {
    return label.replace(/(?:_| |\b)(\w)/g, function ($1) {
      return $1.toUpperCase().replace("_", " ");
    });
  };

  const handleExportSamples = () => {
    const selectedSamples = [];
    let hasSelectedSamples = false;
    // Add inquiry ID as the first row
    selectedSamples.push(["Inquiry ID", formData.inquiry_id]);
    // Add header row
    selectedSamples.push(["Cohort ID", "Inventory Code"]);
    // Iterate through cohorts and their inventory codes
    formData.cohorts.forEach((cohort) => {
      cohort.inventory_code.forEach((invCode) => {
        const invCodeKey = Object.keys(invCode)[0];
        if (invCode[invCodeKey]) {
          selectedSamples.push([cohort.cohort_id, invCodeKey]);
          hasSelectedSamples = true;
        }
      });
    });

    if (!hasSelectedSamples) {
      commonDispatch(
        "warning",
        "No inventory codes selected. Please select at least one inventory code to export."
      );
      return;
    }

    setCsvData(selectedSamples);
    setTimeout(() => {
      csvLinkRef.current.link.click();
    }, 0);
  };

  const gotoManageShipment = () => {
    setValue("five");
    setEditMode(false);
  };

  const getEditHistory = (inquiryId) => {
    if (value === "two" && inquiryId) {
      ShipmentInquiriesService.getInquiryHistory(inquiryId)
        .then((resp) => {
          setHistoryTableData(resp.data);
        })
        .catch((err) => {
          commonDispatch("error", err.message);
        });
    }
  };

  const filteredOptions = () => {
    const statusMap = {
      pending: ["biorep_accepted", rejectStatus],
      biorep_accepted: ["awaiting_approval", rejectStatus],
      awaiting_approval: ["approved", rejectStatus],
      approved: ["pending_shipment", rejectStatus],
      pending_shipment: ["shipped", rejectStatus],
      // shipped: ["completed", rejectStatus],
      default: [rejectStatus],
    };
    return statusMap[formData.status] || statusMap.default;
  };

  useEffect(() => {
    // formData.tracking_number && setTrackingNumber(formData.tracking_number);
    getEditHistory(formData.inquiry_id);
    setEditMode(false);
  }, [value]);

  return (
    <>
      {value === "two" && (
        <Stack sx={{ position: "relative" }}>
          {/* Heading and Pencil icon */}
          <Grid
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              margin: "15px 0px",
              width: "100%",
            }}
          >
            <Typography variant="h5" color="secondary">
              {editMode ? "Edit Shipment Inquiry" : "View Shipment Inquiry"}
            </Typography>

            <Button
              variant="contained"
              onClick={gotoManageShipment}
              sx={{ width: "150px" }}
              disabled={!editMode}
            >
              Manage Shipment
            </Button>
            {!editMode &&
            !(
              formData.status === rejectStatus &&
              formData.last_modified_status === rejectStatus
            ) &&
            formData.status !== "completed" ? (
              <Button
                sx={{
                  position: "absolute",
                  right: "90px",
                  top: "15px",
                }}
                onClick={() => {
                  setEditMode(true);
                }}
              >
                <Tooltip
                  title="Edit Details"
                  style={{
                    cursor: "pointer",
                  }}
                >
                  <EditIcon color={"primary"}></EditIcon>
                </Tooltip>
              </Button>
            ) : null}

            <Button
              variant="outlined"
              onClick={handleBack}
              sx={{
                padding: "5px 10px 5px 0px",
              }}
            >
              <ChevronLeftIcon />
              Go Back
            </Button>
          </Grid>

          {/* Dynamic Form rendering */}
          <Grid container spacing={2}>
            {Object.keys(formData).map((formProperty, index) => {
              return (
                <Fragment key={formProperty}>
                  {/* First Section */}
                  {index < 15 && (
                    <Grid item xs={4}>
                      <TextField
                        label={splitByCapital(formProperty)}
                        name={formProperty}
                        type="text"
                        style={{
                          width: "100%",
                        }}
                        size="small"
                        onChange={(e) => {
                          handleFormFieldsChange(e);
                        }}
                        value={
                          formData[formProperty] ? formData[formProperty] : ""
                        }
                        disabled={
                          !editMode ||
                          formProperty === "inquiry_id" ||
                          formProperty === "status" ||
                          formProperty === "user_id"
                        }
                        required={
                          formProperty !== "address_line_two" ? true : false
                        }
                        error={fieldErrors[formProperty]}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: fieldErrors[formProperty]
                                ? "red"
                                : "inherit",
                            },
                          },
                        }}
                      />
                    </Grid>
                  )}

                  {/* Accordian Section */}
                  {index === 15 && formData[formProperty]?.length > 0 && (
                    <Grid item xs={12}>
                      <Grid container spacing={2} direction="row">
                        {/* Cohort Heading */}
                        <Grid item xs={6}>
                          <Grid item xs={12}>
                            <Grid container spacing={2} direction="row">
                              <Grid item xs={2}>
                                <Typography variant="h5" color="primary">
                                  Cohorts
                                </Typography>
                              </Grid>
                              <Grid item xs={8}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      disabled={!editMode}
                                      name="Check All Inventory Codes"
                                      indeterminate={
                                        formData[formProperty].length > 0 &&
                                        formData[formProperty]?.some((cohort) =>
                                          cohort.inventory_code.some(
                                            (invCode) =>
                                              Object.values(invCode)[0] === true
                                          )
                                        ) &&
                                        formData[formProperty]?.some((cohort) =>
                                          cohort.inventory_code.some(
                                            (invCode) =>
                                              Object.values(invCode)[0] ===
                                              false
                                          )
                                        )
                                      }
                                      checked={
                                        formData[formProperty].length > 0 &&
                                        formData[formProperty].every(
                                          (cohort) =>
                                            cohort.inventory_code.length > 0 &&
                                            cohort.inventory_code.every(
                                              (invCode) =>
                                                Object.values(invCode)[0] ===
                                                true
                                            )
                                        )
                                      }
                                      onChange={handleSelectEveryInventoryCodes}
                                    />
                                  }
                                  label="Check All Inventory Codes"
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>

                        {/* Export List Button */}
                        <Grid item xs={6} sx={{ textAlign: "right" }}>
                          <Button
                            disabled={!editMode}
                            variant="outlined"
                            onClick={handleExportSamples}
                          >
                            Export List of Samples
                          </Button>
                        </Grid>

                        {/* Cohorts details & Inventory Code */}
                        <Grid item xs={12}>
                          {formData[formProperty].map((cohort, ind) => (
                            <Accordion
                              key={ind}
                              sx={{
                                boxShadow: "none",
                                border: "1px solid #c7c7c7",
                                width: "100%",
                                marginBottom: "10px",
                              }}
                            >
                              <CustomAccordionSummary
                                sx={{
                                  height: "30px",
                                  minHeight: "30px",
                                }}
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={
                                  "panel1-content" + { index: ind }
                                }
                                id={"panel1-header" + { index: ind }}
                              >
                                <Typography variant="h6" color="primary">
                                  <span
                                    style={{
                                      fontWeight: "bold",
                                      marginRight: "10px",
                                      fontSize: "14px",
                                    }}
                                  >
                                    Cohort ID :
                                  </span>
                                  {cohort.cohort_id}
                                </Typography>
                              </CustomAccordionSummary>

                              <AccordionDetails sx={{ padding: 2 }}>
                                <Grid
                                  container
                                  spacing={3}
                                  style={{ marginBottom: "10px" }}
                                >
                                  {Object.keys(formData[formProperty][ind])
                                    .filter(
                                      (item) =>
                                        item !== "id" &&
                                        item !== "inquiry_id" &&
                                        item !== "cohort_id" &&
                                        item !== "inventory_code" &&
                                        item !== "created_on" &&
                                        item !== "updated_by"
                                    )
                                    .map((cohortElement, i) => {
                                      return (
                                        <Grid item xs={4} key={i}>
                                          <TextField
                                            label={splitByCapital(
                                              cohortElement
                                            )}
                                            name={cohortElement}
                                            type="text"
                                            style={{
                                              width: "100%",
                                            }}
                                            size="small"
                                            onChange={(e) => {
                                              handleFormFieldsChange(
                                                e,
                                                "cohorts",
                                                formData[formProperty][ind],
                                                ind
                                              );
                                            }}
                                            value={
                                              formData[formProperty][ind][
                                                cohortElement
                                              ]
                                                ? formData[formProperty][ind][
                                                    cohortElement
                                                  ]
                                                : ""
                                            }
                                            disabled={!editMode}
                                            required={isCohortFieldRequired(
                                              cohortElement,
                                              formData[formProperty][ind]
                                                .bio_sample_type
                                            )}
                                            error={
                                              fieldErrors[
                                                `cohort_${ind}_${cohortElement}`
                                              ]
                                            }
                                            sx={{
                                              "& .MuiOutlinedInput-root": {
                                                "& fieldset": {
                                                  borderColor: fieldErrors[
                                                    `cohort_${ind}_${cohortElement}`
                                                  ]
                                                    ? "red"
                                                    : "inherit",
                                                },
                                              },
                                            }}
                                          />
                                        </Grid>
                                      );
                                    })}
                                </Grid>

                                <Grid
                                  container
                                  spacing={3}
                                  style={{ marginBottom: "10px" }}
                                >
                                  <Grid item xs={12}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          disabled={!editMode}
                                          name={cohort.id.toString()}
                                          indeterminate={
                                            cohort.inventory_code.length > 0 &&
                                            cohort.inventory_code.some(
                                              (invCode) =>
                                                Object.values(invCode)[0] ===
                                                true
                                            ) &&
                                            cohort.inventory_code.some(
                                              (invCode) =>
                                                Object.values(invCode)[0] ===
                                                false
                                            )
                                          }
                                          checked={
                                            cohort.inventory_code.length > 0 &&
                                            cohort.inventory_code.every(
                                              (invCode) =>
                                                Object.values(invCode)[0] ===
                                                true
                                            )
                                          }
                                          onChange={(e) =>
                                            handleSelectAllCohortsInventoryCodes(
                                              e,
                                              cohort.cohort_id
                                            )
                                          }
                                        />
                                      }
                                      label="CheckAll"
                                    />
                                  </Grid>

                                  {cohort.inventory_code.map((invCode, i) => {
                                    const invCodeKey = Object.keys(invCode)[0];
                                    const isChecked =
                                      invCode[invCodeKey] === true;
                                    return (
                                      <Fragment key={i}>
                                        <Grid
                                          item
                                          xs={6}
                                          style={{
                                            display: "flex",
                                          }}
                                        >
                                          <FormControlLabel
                                            key={invCodeKey}
                                            control={
                                              <Checkbox
                                                disabled={!editMode}
                                                onChange={() =>
                                                  handleSelectInventoryCode(
                                                    cohort.cohort_id,
                                                    invCode
                                                  )
                                                }
                                                name={invCodeKey}
                                                checked={isChecked}
                                              />
                                            }
                                          />
                                          <TextField
                                            label="Inventory Code"
                                            name={invCodeKey}
                                            type="text"
                                            style={{
                                              width: "100%",
                                            }}
                                            size="small"
                                            onChange={(e) =>
                                              handleInventoryCodeChange(
                                                e,
                                                ind,
                                                i,
                                                isChecked
                                              )
                                            }
                                            value={invCodeKey}
                                            disabled={!editMode}
                                            required={true}
                                            error={
                                              fieldErrors[
                                                `cohort_${ind}_inventory_code`
                                              ]
                                            }
                                            sx={{
                                              "& .MuiOutlinedInput-root": {
                                                "& fieldset": {
                                                  borderColor: fieldErrors[
                                                    `cohort_${ind}_inventory_code`
                                                  ]
                                                    ? "red"
                                                    : "inherit",
                                                },
                                              },
                                            }}
                                          />
                                        </Grid>
                                      </Fragment>
                                    );
                                  })}
                                </Grid>
                              </AccordionDetails>
                            </Accordion>
                          ))}
                        </Grid>
                      </Grid>
                    </Grid>
                  )}

                  {/* Section Headings and horizontal bar */}
                  {index === 2 || index === 14 ? (
                    <Divider
                      style={{
                        width: "100%",
                        marginTop: "20px",
                      }}
                    >
                      {index === 2 && (
                        <Typography variant="h6" color="primary">
                          Shipment Data
                        </Typography>
                      )}
                      {index === 14 && (
                        <Typography variant="h6" color="primary">
                          Shipment Details
                        </Typography>
                      )}
                    </Divider>
                  ) : null}
                </Fragment>
              );
            })}

            {/* Status Droplist and Comment Box */}
            <Grid item xs={12}>
              <Grid container spacing={2} direction="row">
                {/* Status Droplist */}
                <Grid item xs={4}>
                  <Autocomplete
                    id="free-solo-demo"
                    freeSolo
                    size="small"
                    margin="dense"
                    options={filteredOptions()}
                    onChange={(e, new_value) => handleStatusChange(new_value)}
                    renderInput={(params) => (
                      <TextField
                        margin="dense"
                        {...params}
                        required={true}
                        label="Update Status"
                        error={fieldErrors.updateStatus}
                        sx={{
                          margin: 0,
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: fieldErrors.updateStatus
                                ? "red"
                                : "inherit",
                            },
                          },
                        }}
                      />
                    )}
                    value={updateStatus}
                    disabled={formData.last_modified_status !== formData.status}
                  />
                </Grid>

                {/* Comment Box */}
                <Grid item xs={4}>
                  <TextareaAutosize
                    disabled={!editMode}
                    minRows={2}
                    margin="dense"
                    id="Bio Repository Comments"
                    label="Bio Repository Comments"
                    placeholder="Bio Repository Comments"
                    type="text"
                    style={{
                      width: "100%",
                      borderColor: fieldErrors.comment ? "red" : "inherit",
                    }}
                    onChange={handleCommentChange}
                    name="Bio Repository Comments"
                    value={comment}
                    error={fieldErrors.comment}
                  />
                </Grid>

                {/* Tracking Number */}
                {(formData.status === "shipped" ||
                  formData.status === "completed") && (
                  <Grid item xs={4}>
                    <TextField
                      label="Tracking Number"
                      name="Tracking Number"
                      type="text"
                      style={{
                        width: "100%",
                      }}
                      size="small"
                      onChange={(e) =>
                        handleTrackingNumberChange(e.target.value)
                      }
                      value={
                        formData.tracking_number
                          ? formData.tracking_number
                          : trackingNumber
                      }
                      disabled={!editMode}
                      required={true}
                    />
                  </Grid>
                )}

                {/* Save Button */}

                <Grid
                  container
                  sx={{ justifyContent: "space-around", marginTop: "30px" }}
                >
                  <Grid size={{ xs: 4 }}>
                    <Button
                      disabled={
                        !editMode ||
                        formData.last_modified_status !== formData.status
                      }
                      variant="contained"
                      onClick={handleSubmit}
                      sx={{ width: "120px" }}
                    >
                      Save
                    </Button>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Button
                      variant="outlined"
                      onClick={handleSendToBAH}
                      sx={{ width: "120px" }}
                      disabled={
                        !editMode ||
                        formData.last_modified_status === formData.status
                      }
                    >
                      Send To BAH
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Divider
            style={{
              width: "100%",
              margin: "30px 0px",
            }}
          >
            <Typography variant="h6" color="primary">
              Inquiry Edit History
            </Typography>
          </Divider>

          {/* Changes History Table */}
          <Stack sx={{ display: "grid", justifyContent: "center" }}>
            <TableContainer>
              <Table
                aria-label="Inquiry Status History Table"
                size={"small"}
                variant="solid"
                sx={{
                  border: "1px solid rgba(224, 224, 224, 1)",
                  "& td, & th": {
                    border: "1px solid rgba(224, 224, 224, 1)",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {historyTableColumns.map((row, index) => (
                      <StyledTableCell align="left" key={index}>
                        {row}
                      </StyledTableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyTableData.length > 0 ? (
                    historyTableData
                      .slice(
                        pageLarge * rowsPerPageLarge,
                        pageLarge * rowsPerPageLarge + rowsPerPageLarge
                      )
                      .map((row, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            "&:last-child td, &:last-child th": {
                              border: "1px solid rgba(224, 224, 224, 1)",
                            },
                          }}
                        >
                          <TableCell align="left">
                            {new Date(row.created_on).toLocaleDateString() ||
                              "None / Null"}
                          </TableCell>
                          <TableCell align="left">
                            {row.new_data || "None / Null"}
                          </TableCell>
                          <TableCell align="left">
                            {row.created_by || "None / Null"}
                          </TableCell>
                          <TableCell align="left">
                            {row.comments || "No Comment"}
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell
                        align="center"
                        colSpan={historyTableColumns.length + 2}
                        sx={{ border: "1px solid rgba(224, 224, 224, 1)" }}
                      >
                        No History Found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[6, 10, 25]}
                component="div"
                count={historyTableData.length}
                rowsPerPage={rowsPerPageLarge}
                page={pageLarge}
                onPageChange={handleChangePageLarge}
                onRowsPerPageChange={handleChangeRowsPerPageLarge}
              />
            </TableContainer>
          </Stack>
        </Stack>
      )}

      {/* Trigger CSV download */}
      <CSVLink
        data={csvData}
        filename={`sample_list_${formData.inquiry_id}.csv`}
        className="hidden"
        ref={csvLinkRef}
        target="_blank"
      />
    </>
  );
};

export default InquiryDetails;
